import { Component, OnInit, ElementRef, HostListener, ViewChild, DestroyRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { AbstractControl, FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Storage, ref, getDownloadURL } from '@angular/fire/storage';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { NgOtpInputModule } from 'ng-otp-input';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

import { ApiService } from 'src/app/services/api.service';
import { NotificationService } from 'src/app/services/notification.service';
import { SessionServiceService } from '../../services/session-service.service';
import { FormatFileSizePipe } from '../../format-file-size.pipe';
import { FooterComponent } from '../footer/footer.component';

declare var $: any;

/** Shape of a single file inside the transfer (parsed from `results.files`). */
interface TransferFile {
  name: string;
  path: string;
  type: string;
  size: number | string;
  hash?: string;
  download?: boolean;
  downloadRemaining?: string;
  downloadDataspeed?: number;
}

/** Progress callback emitted while streaming a file. */
type DownloadProgress = (progress: number, elapsedTime: number, speed: number) => void;

@Component({
  selector: 'app-download',
  templateUrl: './download.component.html',
  styleUrls: ['./download.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    NgOtpInputModule,
    NgbModule,
    FormatFileSizePipe,
    FooterComponent
  ]
})
export class DownloadComponent implements OnInit {

  // ---------------------------------------------------------------------------
  // Route / transfer identity
  // ---------------------------------------------------------------------------
  key: any = '';
  auth: any = '';
  url = '';
  prev: any= '';
  currentUrl = '';
  currentUrlLocation = '';

  // ---------------------------------------------------------------------------
  // Transfer data
  // ---------------------------------------------------------------------------
  senderData: any;
  results: any = [];
  // Kept as `any[]` (matching the JSON payload) so the template can bind to
  // dynamic, per-download fields such as `progress` / `downloadRemaining`.
  fileList: any[] = [];
  fileDownloadUrls: string[] = [];
  totalFileSize = 0;
  addClass = 'addClass';

  // ---------------------------------------------------------------------------
  // Branding (loaded from the freelancer/company profile)
  // ---------------------------------------------------------------------------
  currentUser: any = '';
  logoUrl: any = '';
  companyName: any = '';
  companyOwnWebsite: any = '';
  borderTopColor: any = '';
  borderBottomColor: any = '';
  buttonBGselectedColor: any = '';

  // ---------------------------------------------------------------------------
  // Page state
  // ---------------------------------------------------------------------------
  loader = true;
  loadingStage = 1;
  isTransferDisabled = false;
  lanName: any = localStorage.getItem('net_lang_new') ?? 'jp';
  getLang: any = localStorage.getItem('net_lang_new');

  // ---------------------------------------------------------------------------
  // Verification state
  // ---------------------------------------------------------------------------
  isVerified = false;
  isDownloadCodeEnabled = false;
  isEmailVerificationEnabled = false;
  isSmsVerificationEnabled = false;
  isEmailAndSmsVerificationEnabled = false;
  verificationType: 'email' | 'sms' = 'email';

  downloadUserEmail = '';
  downloadUserContact = '';

  // ---------------------------------------------------------------------------
  // OTP / security-code inputs
  // ---------------------------------------------------------------------------
  sendOtpForm!: FormGroup;
  isOtpFormSubmitted = false;
  isOtpSent = false;

  /**
   * 7-digit access code (auth-link flow). Alphanumeric and case-sensitive —
   * accepts uppercase letters, lowercase letters and digits (e.g. `aB3xK9z`).
   */
  downloadCode = '';
  isDownloadCodeComplete = false;
  downloadCodeConfig = this.buildOtpConfig(7, false);

  /** 4-digit OTP (email/SMS flow). */
  emailDownloadCode = '';
  isEmailOtpComplete = false;
  emailOtpConfig = this.buildOtpConfig(4);
  emailOtpWrongAttempts = 0;
  isEmailOtpBlocked = false;
  readonly maxEmailOtpAttempts = 3;

  /** 5-minute countdown for the OTP's validity. */
  readonly emailOtpDurationSeconds = 5 * 60;
  emailOtpTimeLeft = this.emailOtpDurationSeconds;
  emailOtpExpired = false;
  private emailOtpTimerSub?: Subscription;

  // ---------------------------------------------------------------------------
  // Download progress / limits
  // ---------------------------------------------------------------------------
  progress: any = [];
  remainingTimeData: string | null = null;
  downloadCount = 0;

  private _isDownloadDisabled = false;
  get isDownloadDisabled(): boolean {
    return this._isDownloadDisabled;
  }
  set isDownloadDisabled(value: boolean) {
    this._isDownloadDisabled = value;
    if (!this.sendOtpForm) return;
    const emailCtrl = this.sendOtpForm.controls['email'];
    if (value) {
      emailCtrl.disable();
    } else if (this.verificationType === 'email') {
      emailCtrl.enable();
    }
  }

  // ---------------------------------------------------------------------------
  // Downloader location info (only fetched when enabled for the transfer)
  // ---------------------------------------------------------------------------
  ipaddress = '';
  city = '';
  state = '';
  country = '';

  // ---------------------------------------------------------------------------
  // Video preview / streaming
  // ---------------------------------------------------------------------------
  @ViewChild('preloadVid') preloadVid!: ElementRef<HTMLVideoElement>;
  @ViewChild('myVideo') player?: ElementRef<HTMLVideoElement>;
  fileDurations: string[] = [];
  videoViewURL = '';
  isBuffering = true;
  watermarkText = '';
  private currentUrlStream: string | null = null;
  private readyByUrl = new Map<string, boolean>();

  // ---------------------------------------------------------------------------
  // Device detection
  // ---------------------------------------------------------------------------
  isIosTouchDevice = false;

  // ---------------------------------------------------------------------------
  // Misc
  // ---------------------------------------------------------------------------
  private sessionValue: any = '';

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    public translateService: TranslateService,
    private toast: NotificationService,
    private sessionService: SessionServiceService,
    private formBuilder: FormBuilder,
    private storage: Storage,
    private titleService: Title,
    private apiService: ApiService,
    private destroyRef: DestroyRef,
    private cdr: ChangeDetectorRef
  ) {}

  // ===========================================================================
  // Lifecycle
  // ===========================================================================
  ngOnInit(): void {
    this.detectIosTouchDevice();
    this.sessionValue = this.sessionService.startSession();

    $('.sidebarMenuScroll').slimScroll({
      height: 'calc(100vh - 220px)',
      color: '#d6dbe6',
      alwaysVisible: false,
      size: '8px',
      distance: '1px',
      railVisible: false,
      railColor: '#d6dbe6',
      disableFadeOut: true,
      wheelStep: 8
    });

    this.sendOtpForm = this.formBuilder.group({
      email: [null, [Validators.required, Validators.email]],
      sms: [null, [Validators.required, Validators.minLength(10), Validators.maxLength(12), Validators.pattern('^[0-9]*$')]]
    });
    this.sendOtpForm.controls['sms'].disable();

    this.resolveTransferFromUrl();

    // Show progressively reassuring messages while a slow transfer loads.
    setTimeout(() => (this.loadingStage = 2), 5000);
    setTimeout(() => (this.loadingStage = 3), 15000);
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnloadHandler(_event: Event): void {
    this.sessionService.removeSession();
  }

  /** Block Ctrl/Cmd+S so the page isn't accidentally saved (light deterrent only). */
  @HostListener('document:keydown', ['$event'])
  onKeydown(e: KeyboardEvent): void {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
      e.preventDefault();
    }
  }

  // ===========================================================================
  // Transfer loading
  // ===========================================================================
  /** Reads `key`/`auth` from the URL and routes to the matching download flow. */
  private resolveTransferFromUrl(): void {
    this.activatedRoute.queryParams.subscribe(params => {
      this.auth = params['auth'];

      let rawKey: string = params['key'] ?? '';
      let prevParam: string | undefined = params['prev'];

      if (rawKey.includes('?')) {
        const [keyPart, ...rest] = rawKey.split('?');
        rawKey = keyPart;
        const extra = new URLSearchParams(rest.join('&'));
        if (prevParam == null && extra.has('prev')) {
          prevParam = extra.get('prev') ?? undefined;
        }
      }

      this.key = rawKey;
      // Store as a 1/0 flag: 1 when `prev=enable`, 0 otherwise.
      this.prev = prevParam === 'enable' ? 1 : 0;

      this.currentUrl = window.location.origin;
      this.currentUrlLocation = window.location.href;
      this.url = window.location.host;
      // this.url = 'whitepointindia.net0.download';
      // this.url = 'dextrosoft.filetransfercenter.com';
      // this.url = 'dextrosoft.net.download';

      if (this.auth) {
        // 7-digit access-code flow.
        this.getFreelancerBrandDetails('');
        this.isDownloadCodeEnabled = true;
      } else if (this.key) {
        // Direct/email/SMS flow.
        this.loadTransferConfig({
          key: this.key,
          encrypted_keycode: '',
          digiTcode: '',
          brand_url: this.url,
          access_from_download: 1,
          is_preview : this.prev
        });
      } else if (this.prev) {
        // Direct/email/SMS flow.
        this.loadTransferConfig({
          key: this.key,
          encrypted_keycode: '',
          digiTcode: '',
          brand_url: this.url,
          access_from_download: 1,
          is_preview : 1
        });
      }else {
        this.router.navigate(['/transfer-expired']);
      }
    });
  }

  /**
   * Loads the transfer *configuration* only (no file paths). The file list is
   * fetched separately via `fetchFiles()` — immediately for a direct link, or
   * after successful OTP verification — so file URLs are never exposed to an
   * unverified visitor.
   */
  loadTransferConfig(jSon: any): void {
    this.fileDownloadUrls = [];
    this.totalFileSize = 0;

    this.apiService.loadConfig(jSon).subscribe(
      res => {
        this.senderData = res;

        if (res.success == 1) {
          this.results = this.senderData.data;

          if (this.results.is_expired == '1') {
            this.router.navigate(['/transfer-expired']);
          } else if (this.results.valid_user == '0') {
            this.router.navigate(['/page-not-found']);
          } else {
            this.getFreelancerBrandDetails(this.results.keycode);
            this.applyDownloaderInfoVisibility();

            this.logOperation('送信先ユーザ が ' + this.currentUrlLocation + ' をクリックしました。');

            this.applyVerificationFlags();
            this.applyDownloadLimit(this.results.no_of_downloads, this.results.download_limit);
            this.isTransferDisabled = this.results.transfer_status != 1;

            // Direct link (no verification required): files can be loaded now.
            // Otherwise they are loaded only after OTP verification succeeds.
            if (this.isVerified) {
              this.fetchFiles(jSon);
            }
          }
        } else if (res.success == 2) {
          this.router.navigate(['/transfer-expired']);
        } else {
          this.router.navigate(['/page-not-found']);
        }
      },
      err => console.error(err)
    );
  }

  /**
   * Fetches the authorized file list and populates the download UI. Called only
   * once the visitor is allowed to see the files (direct link or verified OTP).
   */
  private fetchFiles(jSon: any): void {
    this.fileDownloadUrls = [];
    this.totalFileSize = 0;

    this.apiService.getTransferDetails(jSon).subscribe(
      res => {
        this.senderData = res;
        if (this.senderData && this.senderData.data) {
          this.results = this.senderData.data;
          this.populateFileList(this.senderData.data.files);
        }
      },
      err => console.error(err)
    );
  }

  /** Sets which verification UI (email / SMS / both / direct) should be shown. */
  private applyVerificationFlags(): void {
    const emailRequired = this.results.is_email_verified == 1;
    const smsRequired = this.results.is_sms_enabled == 1;

    // `isEmailVerificationEnabled` / `isSmsVerificationEnabled` are true whenever
    // that channel is required (including the "both" case); the template uses all
    // three flags together to decide when to show the email/SMS toggle tabs.
    this.isEmailVerificationEnabled = emailRequired;
    this.isSmsVerificationEnabled = smsRequired;
    this.isEmailAndSmsVerificationEnabled = emailRequired && smsRequired;

    // Direct download (no OTP step) only when no verification is required.
    this.isVerified = !emailRequired && !smsRequired;

    // Default to the email tab unless only SMS verification is required.
    this.verificationType = (smsRequired && !emailRequired) ? 'sms' : 'email';
    if (this.verificationType === 'email') {
      this.sendOtpForm.controls['email'].enable();
      this.sendOtpForm.controls['sms'].disable();
    } else {
      this.sendOtpForm.controls['sms'].enable();
      this.sendOtpForm.controls['email'].disable();
    }
  }

  /** Disables downloading once the download limit has been reached. */
  private applyDownloadLimit(downloads: any, limit: any): void {
    this.isDownloadDisabled = downloads == limit;
  }

  /**
   * Only load the geo-IP plugin / fetch location when the transfer is
   * configured to capture downloader info. Must run for every flow that
   * populates `this.results` (direct link and 7-digit access code) — otherwise
   * `ipaddress`/`city`/`state`/`country` stay empty and the download
   * notification is sent without them.
   */
  private applyDownloaderInfoVisibility(): void {
    if (this.results.is_downloader_info_enabled != 0) {
      this.getLocations();
    } else {
      this.ipaddress = this.translateService.instant('informations_restricted');
      this.city = this.translateService.instant('informations_restricted');
      this.state = this.translateService.instant('informations_restricted');
      this.country = this.translateService.instant('informations_restricted');
    }
  }

  /** Parses the `files` JSON, then computes the URL list and total size. */
  private populateFileList(filesJson: string): void {
    this.fileDownloadUrls = [];
    this.totalFileSize = 0;
    this.fileList = JSON.parse(filesJson);

    for (const file of this.fileList) {
      this.totalFileSize += Number(file.size);
      this.fileDownloadUrls.push(file.path);
    }
  }

  // ===========================================================================
  // Verification (OTP / security code)
  // ===========================================================================
  get otpFormControls(): { [key: string]: AbstractControl } {
    return this.sendOtpForm.controls;
  }

  /** Sends an email or SMS OTP for the email/SMS verification flow. */
  sendOtpForVerification(): void {
    this.isOtpFormSubmitted = true;
    if (this.sendOtpForm.invalid) return;

    const jSon = this.verificationType === 'email'
      ? { key: this.key, email: this.sendOtpForm.controls['email'].value, lang_type: this.getLang, contact_type: 'email' }
      : { key: this.key, phno: this.sendOtpForm.controls['sms'].value, lang_type: this.getLang, contact_type: 'sms' };

    this.downloadUserEmail = this.sendOtpForm.controls['email'].value;
    this.downloadUserContact = this.sendOtpForm.controls['sms'].value;

    this.apiService.sendOtpForDownload(jSon).subscribe((res: any) => {
      this.handleSendOtpResponse(res);
    }, err => console.error(err));
  }

  /** Maps the OTP send response codes to user-facing messages. */
  private handleSendOtpResponse(res: any): void {
    const t = (k: string) => this.translateService.instant(k);

    switch (res.success) {
      case 1:
        this.isOtpSent = true;
        this.startEmailOtpTimer();
        this.toast.showSuccess(t('otp_send_successfully'), '');
        this.logOperation('ダウンロード認証用OTPが送信されました。'); // OTP for download verification sent.
        break;
      case 6:
        this.isOtpSent = true;
        this.startEmailOtpTimer();
        this.toast.showSuccess(t('otp_sent_to_phone_number'), '');
        break;
      case 4:
        this.toast.showError(t('your_email_is_mismatched'), t('error'));
        break;
      case 5:
      case 8:
        this.toast.showInfoLonger(t('freelancer_otp_limit_reached_description'), '');
        break;
      case 9:
        this.toast.showError(t('your_sms_is_mismatched'), t('error'));
        break;
      default:
        // this.toast.showError(t('unknown_error'), t('error'));
        this.toast.showError(t('your_email_is_mismatched'), t('error'));
    }
  }

  onOtpChange(code: string): void {
    this.isDownloadCodeComplete = code.length === 7;
    if (this.isDownloadCodeComplete) {
      this.downloadCode = code;
    }
  }

  onEmailOtpChange(code: string): void {
    this.isEmailOtpComplete = code.length === 4;
    if (this.isEmailOtpComplete) {
      this.emailDownloadCode = code;
    }
  }

  /** Verifies the 4-digit OTP, then loads the transfer files on success. */
  checkEmailOtp(): void {
    if (this.isEmailOtpBlocked || this.emailOtpExpired) {
      return;
    }

    this.apiService.checkOtpForDownload({ key: this.key, otp: this.emailDownloadCode }).subscribe((res: any) => {
      if (res.success != 1) {
        this.emailOtpWrongAttempts++;

        if (this.emailOtpWrongAttempts >= this.maxEmailOtpAttempts) {
          this.isEmailOtpBlocked = true;
        } else {
          this.toast.showError(this.translateService.instant('invalid_code'), '');
        }
        return;
      }

      this.stopEmailOtpTimer();
      this.isVerified = true;
      this.isEmailVerificationEnabled = false;
      this.isSmsVerificationEnabled = false;
      this.isEmailAndSmsVerificationEnabled = false;

      // Verification passed — now (and only now) fetch the actual files.
      this.fetchFiles({
        key: this.key,
        encrypted_keycode: '',
        digiTcode: '',
        // brand_url: 'farha.net0.download',
        access_from_download: 1,
        brand_url: this.url,
        is_preview: this.prev
      });

      this.logOperation('ダウンロード認証用OTPが承認されました。'); // OTP for download verification approved.

      this.apiService.getLinkAccessCount({
        email: this.sendOtpForm.controls['email'].value,
        keycode: this.key,
        is_preview: this.prev
      }).subscribe({ error: err => console.error(err) });
    }, err => console.error(err));
  }

  /** Reloads the page after the OTP attempt limit is reached. */
  reloadPage(): void {
    window.location.reload();
  }

  /** Starts (or restarts) the 5-minute countdown for the OTP's validity. */
  private startEmailOtpTimer(): void {
    this.emailOtpTimerSub?.unsubscribe();
    this.emailOtpExpired = false;
    this.emailOtpTimeLeft = this.emailOtpDurationSeconds;

    this.emailOtpTimerSub = interval(1000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.emailOtpTimeLeft--;
        if (this.emailOtpTimeLeft <= 0) {
          this.emailOtpExpired = true;
          this.emailOtpTimerSub?.unsubscribe();
        }
      });
  }

  private stopEmailOtpTimer(): void {
    this.emailOtpTimerSub?.unsubscribe();
  }

  /** Formats the remaining OTP validity time as mm:ss. */
  get emailOtpTimeDisplay(): string {
    const minutes = Math.floor(this.emailOtpTimeLeft / 60);
    const seconds = this.emailOtpTimeLeft % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  }

  /** Verifies the 7-digit access code (auth-link flow). */
  checkOtp(): void {
    $('.checkOtp').html('<i class="fa fa-spinner fa-spin"></i>');

    const code = {
      key: '',
      encrypted_keycode: this.auth,
      digiTcode: this.downloadCode,
      brand_url: this.url,
      // brand_url: "farha.net0.download",
      access_from_download: 1,
      is_preview: 0
    };

    this.apiService.getTransferDetails(code).subscribe((res: any) => {
      if (res.success != 1) {
        this.toast.showError(this.translateService.instant('invalid_code'), '');
        $('.checkOtp').html(this.translateService.instant('next'));
        return;
      }

      this.isVerified = true;
      this.isDownloadCodeEnabled = false;
      this.senderData = res;
      this.results = this.senderData.data;
      this.key = this.results.keycode;

      this.isTransferDisabled = this.results.transfer_status != 1;
      this.applyDownloadLimit(this.senderData.data.no_of_downloads, this.senderData.data.download_limit);
      this.applyDownloaderInfoVisibility();
      this.logOperation('7桁のセキュリティコードが承認されました。'); // 7-digit security code approved.

      if (this.senderData.data.is_expired == '1') {
        this.router.navigate(['/transfer-expired']);
      } else {
        this.populateFileList(this.senderData.data.files);
      }
    }, err => console.error(err));
  }

  /** Switches between the email and SMS verification tabs. */
  toggleVerificationMode(type: 'email' | 'sms'): void {
    if ($('#selected_' + type).hasClass('selected')) {
      $('.commonToggleClass').removeClass('selected');
    } else {
      $('.commonToggleClass').removeClass('selected');
      $('#selected_' + type).addClass('selected');
    }

    this.verificationType = type;

    if (type === 'email') {
      $('.emailContainerBox').removeClass('d-none');
      $('.smsContainerBox').addClass('d-none');
      this.sendOtpForm.controls['sms'].disable();
      this.sendOtpForm.controls['email'].enable();
    } else {
      $('.smsContainerBox').removeClass('d-none');
      $('.emailContainerBox').addClass('d-none');
      this.sendOtpForm.controls['sms'].enable();
      this.sendOtpForm.controls['email'].disable();
    }
  }

  // ===========================================================================
  // Single-file download
  // ===========================================================================
  async downloadFile(url: string, _fileType: any, fileName: string, custClass: string, downloadNotice: any, index: number): Promise<void> {
    this.apiService.updateDownloadClickCount({ keycode: this.results.keycode, is_preview: this.prev }).subscribe({ error: err => console.error(err) });

    const fileSizeInBytes = Number(this.fileList[index]?.size || 0);
    const LARGE_FILE_THRESHOLD = 524288000; // 500 MB
    const isLargeFile = fileSizeInBytes > LARGE_FILE_THRESHOLD;

    // iOS/iPadOS can't stream large blobs reliably: notify first, then hand off
    // to the browser's native downloader immediately inside the click event.
    if (this.isIosTouchDevice && isLargeFile) {
      this.logOperationArray([this.buildFileDownloadLog(fileName)]);
      this.bumpDownloadCount();
      this.downloadNotice(downloadNotice, fileName);
      this.downloadFileForIos(url, fileName);
      return;
    }

    try {
      const progressBar = this.toggleFileProgressUi(custClass, true);
      this.progress = [];

      await this.downloadFileWithProgress(url, fileName, (progress, elapsedTime, speed) => {
        this.remainingTimeData = this.calculateRemainingTime(progress, elapsedTime);
        this.progress[index] = { remaining_time: this.remainingTimeData, remaining_progress: progress };
        this.fileList[index].downloadRemaining = this.remainingTimeData ?? '';
        this.fileList[index].downloadDataspeed = speed;
        this.renderProgressRing(custClass, progressBar, progress);
      });

      // Only record the download and notify once it has actually completed.
      this.logFileDownloadAndNotify(fileName, downloadNotice);
    } catch (error) {
      console.error('Error downloading file:', error);
      this.toggleFileProgressUi(custClass, false);
    }
  }

  /** Shows the circular progress UI for a file button (returns the bar refs). */
  private toggleFileProgressUi(custClass: string, downloading: boolean): { left: any; right: any } {
    $('.' + custClass).prop('disabled', downloading);
    $('.' + custClass + ' .downloadIcon').toggleClass('d-none', downloading);
    $('.' + custClass + ' .progress').toggleClass('d-none', !downloading);
    return {
      left: $('.' + custClass + ' .progress').find('.progress-left .progress-bar'),
      right: $('.' + custClass + ' .progress').find('.progress-right .progress-bar')
    };
  }

  /** Rotates the two halves of the circular progress ring to match `progress`. */
  private renderProgressRing(custClass: string, bar: { left: any; right: any }, progress: number): void {
    const degrees = (progress / 100) * 360;
    $('.' + custClass + ' .progress-value').html(progress.toFixed(0) + '%');

    if (progress > 0 && progress < 100) {
      if (degrees <= 180) {
        bar.right.css('transform', 'rotate(' + degrees + 'deg)');
      } else {
        bar.right.css('transform', 'rotate(183deg)');
        bar.left.css('transform', 'rotate(' + (degrees - 180) + 'deg)');
      }
    } else if (progress === 100) {
      $('.' + custClass + ' .downloadIcon').removeClass('d-none');
      $('.' + custClass + ' .progress').addClass('d-none');
      bar.right.css('transform', 'rotate(0deg)');
      bar.left.css('transform', 'rotate(0deg)');
      $('.' + custClass).prop('disabled', false);
    }
  }

  /** Logs a completed single-file download and sends the download notice. */
  private logFileDownloadAndNotify(fileName: string, downloadNotice: any): void {
    this.apiService.saveOperationLogArray(this.buildOperationLogArray([this.buildFileDownloadLog(fileName)])).subscribe({
      next: (res: any) => {
        if (res.success == 1) {
          this.bumpDownloadCount();
          this.downloadNotice(downloadNotice, fileName);
        }
      },
      error: err => console.error('API Error:', err)
    });
  }

  // ===========================================================================
  // Download all (ZIP)
  // ===========================================================================
  async downloadAllFiles(downloadNotice: any, fileName: string, fileListLength: number, firstFile: TransferFile): Promise<void> {
    // A single file doesn't need zipping.
    if (fileListLength <= 1) {
      this.downloadFile(this.fileList[0].path, this.fileList[0].type, this.fileList[0].name, 'addClass0', this.results.is_download_notice_enabled, 0);
      return;
    }

    this.apiService.updateDownloadClickCount({ keycode: this.results.keycode, is_preview: this.prev }).subscribe({ error: err => console.error(err) });

    $('.downloadClickBtn').addClass('d-none');
    $('.downloadStartBtn').removeClass('d-none');
    $('.downloadStartBtn').html('<i class="fa fa-spinner fa-spin"></i> ' + this.translateService.instant('preparing_download'));
    $('.dallBtn').prop('disabled', true);

    const zipFilename = this.currentTimestampString() + '.zip';
    const logString = `${this.downloaderName()} が ${firstFile.name}...(zip) をダウンロードしました。(${this.locationLabel()})`;

    // iOS/iPadOS: notify before the download starts.
    if (this.isIosTouchDevice) {
      this.apiService.saveOperationLogArray(this.buildOperationLogArray([logString])).subscribe((res: any) => {
        if (res.success == 1) this.bumpDownloadCount();
      }, err => console.error(err));
      await this.downloadNotice(downloadNotice, fileName);
    }

    try {
      await this.createZipAndDownload(zipFilename);
      this.resetDownloadAllButton();

      // Non-iOS devices: notify after the download completes.
      if (!this.isIosTouchDevice) {
        this.bumpDownloadCount();
        await this.downloadNotice(downloadNotice, fileName);
        this.apiService.saveOperationLogArray(this.buildOperationLogArray([logString])).subscribe({ error: err => console.error(err) });
      }
    } catch (error) {
      console.error('Download error:', error);
      this.toast.showError(this.translateService.instant('download_failed'), this.translateService.instant('error'));
      this.resetDownloadAllButton();
    }
  }

  private resetDownloadAllButton(): void {
    $('.downloadAll').html('');
    $('.downloadClickBtn').removeClass('d-none');
    $('.downloadStartBtn').addClass('d-none');
    $('.dallBtn').prop('disabled', false);
  }

  /** Downloads every file (max 5 in parallel), zips client-side, and saves it. */
  private async createZipAndDownload(zipFilename: string): Promise<void> {
    const zip = new JSZip();
    const totalFiles = this.fileDownloadUrls.length;
    let completedFiles = 0;
    const CONCURRENT_LIMIT = 5;

    const updateProgress = () => {
      const percentage = Math.round((completedFiles / totalFiles) * 100);
      $('.downloadStartBtn').html(
        `<i class="fa fa-spinner fa-spin"></i> ${this.translateService.instant('downloading')} (${percentage}%)`
      );
    };

    // Phase 1: download files in batches.
    for (let i = 0; i < this.fileDownloadUrls.length; i += CONCURRENT_LIMIT) {
      const batch = this.fileDownloadUrls.slice(i, i + CONCURRENT_LIMIT);
      await Promise.all(batch.map(async (url: string) => {
        try {
          const storageRef = ref(this.storage, url);
          const downloadUrl = await getDownloadURL(storageRef);
          const response = await fetch(downloadUrl);
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          zip.file(storageRef.name, await response.blob());
        } catch (error) {
          console.error('Failed to download file:', error);
        } finally {
          completedFiles++;
          updateProgress();
        }
      }));
    }

    // Phase 2: generate the ZIP.
    $('.downloadStartBtn').html(`<i class="fa fa-spinner fa-spin"></i> ${this.translateService.instant('creating_zip')} 0%`);
    const zipBlob = await zip.generateAsync(
      { type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } },
      (metadata: any) => {
        $('.downloadStartBtn').html(
          `<i class="fa fa-spinner fa-spin"></i> ${this.translateService.instant('creating_zip')} ${Math.round(metadata.percent)}%`
        );
      }
    );

    // Phase 3: hand the blob to the browser.
    $('.downloadStartBtn').html(`<i class="fa fa-spinner fa-spin"></i> ${this.translateService.instant('starting_download')}`);
    this.saveBlob(zipBlob, zipFilename);
  }

  /** Saves a blob, using FileSaver on iOS for compatibility. */
  private saveBlob(blob: Blob, filename: string): void {
    if (this.isIosTouchDevice) {
      saveAs(blob, filename);
      return;
    }
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  // ===========================================================================
  // Low-level download helpers
  // ===========================================================================
  /** Resolves a storage path to a URL and streams it with progress reporting. */
  private async downloadFileWithProgress(filePath: string, fileName: string, progressCallback: DownloadProgress): Promise<void> {
    const storageRef = ref(this.storage, filePath);
    const url = await getDownloadURL(storageRef);
    await this.streamFile(url, fileName, progressCallback);
  }

  /** Streams a file via fetch, reporting progress, and triggers the download. */
  private async streamFile(url: string, fileName: string, progressCallback: DownloadProgress): Promise<void> {
    const response = await fetch(url);
    const contentLength = response.headers.get('content-length');
    if (!contentLength) {
      throw new Error('Content-Length response header is missing');
    }

    const total = parseInt(contentLength, 10);
    let loaded = 0;
    const startTime = Date.now();
    const reader = response.body?.getReader();

    const stream = new ReadableStream({
      start(controller) {
        const push = () => {
          reader?.read().then(({ done, value }) => {
            if (done) {
              controller.close();
              return;
            }
            loaded += value?.byteLength || 0;
            const progress = Math.round((loaded / total) * 100);
            const elapsedTime = Date.now() - startTime;
            const speed = loaded / (elapsedTime / 1000); // bytes/second
            progressCallback(progress, elapsedTime, speed);
            controller.enqueue(value);
            push();
          });
        };
        push();
      }
    });

    const blob = await new Response(stream).blob();
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(link.href);
  }

  /**
   * iOS/iPadOS large-file download: a window must be opened synchronously inside
   * the click event, then redirected to the resolved URL once it's available.
   */
  private downloadFileForIos(filePath: string, fileName: string): void {
    const downloadWindow = window.open('about:blank', '_blank');
    const storageRef = ref(this.storage, filePath);

    getDownloadURL(storageRef).then(url => {
      const separator = url.includes('?') ? '&' : '?';
      const downloadUrl = `${url}${separator}response-content-disposition=attachment;filename="${encodeURIComponent(fileName)}"`;
      if (downloadWindow) {
        downloadWindow.location.href = downloadUrl;
      }
    }).catch(error => {
      console.error('Error getting download URL:', error);
      downloadWindow?.close();
      this.toast.showError(this.translateService.instant('download_failed'), this.translateService.instant('error'));
    });
  }

  // ===========================================================================
  // Notifications & operation logs
  // ===========================================================================
  /** Sends a download notification (or anonymous tracking when notice is off). */
  downloadNotice(downloadNoticeEnable: any, fileName: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const noticeEnabled = downloadNoticeEnable == 1;

      const downloadPerson = noticeEnabled
        ? (this.verificationType === 'email' ? this.downloadUserEmail : this.downloadUserContact)
        : 'Unknown';

      const notificationJson = {
        download_person: this.ipaddress !=''?downloadPerson:'',
        composed_id: this.key,
        // notify_email: noticeEnabled ? this.results.email : '',
        // notify_person_name: noticeEnabled ? this.results.fullname : '',
        ipaddress: this.ipaddress,
        city: this.city,
        country: this.country,
        state: this.state,
        filename: fileName,
        downloadCount: this.downloadCount,
        is_preview: this.prev
      };

      const request$ = noticeEnabled
        ? this.apiService.sendDownloadNotification(notificationJson)
        : this.apiService.downloadTracking(notificationJson);

      request$.subscribe((res: any) => {
        if (res.success == 1) {
          // this.isDownloadDisabled = !(res.remaining_download_limit > 0);
          resolve();
        } else {
          reject({});
        }
      }, err => {
        console.error(err);
        reject(err);
      });
    });
  }

  /** Persists a single operation-log line. */
  private logOperation(text: string): void {
    const payload = { key: this.results.keycode, texted_string: text, created_at: Date.now() };
    this.apiService.saveOperationLog(payload).subscribe({ error: err => console.error(err) });
  }

  /** Persists an array of operation-log lines. */
  private logOperationArray(texts: string[]): void {
    this.apiService.saveOperationLogArray(this.buildOperationLogArray(texts)).subscribe({ error: err => console.error(err) });
  }

  private buildOperationLogArray(texts: string[]): any {
    return { key: this.results.keycode, texted_string: texts, created_at: Date.now() };
  }

  /** "<downloader> が <fileName> をダウンロードしました。(<location>)" */
  private buildFileDownloadLog(fileName: string): string {
    return `${this.downloaderName()} が ${fileName} をダウンロードしました。(${this.locationLabel()})`;
  }

  private downloaderName(): string {
    const email = this.sendOtpForm.controls['email'].value;
    return email ? email + ' ' : '送信先ユーザ ';
  }

  private locationLabel(): string {
    return this.ipaddress == 'Not Available' ? 'Location Not Available' : this.ipaddress;
  }

  /** Increments the download count once per active session. */
  private bumpDownloadCount(): void {
    if (this.sessionValue == this.sessionService.startSession()) {
      this.downloadCount++;
    }
  }

  // ===========================================================================
  // Video preview
  // ===========================================================================
  setDuration(video: HTMLVideoElement, index: number): void {
    const seconds = Math.floor(video.duration);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    this.fileDurations[index] = `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  /** Warms the buffer for a video on hover so playback starts instantly. */
  primeVideo(url: string): void {
    if (!url || this.readyByUrl.get(url)) return;
    const v = this.preloadVid?.nativeElement;
    if (!v) return;

    if (v.src !== url) v.src = url;
    v.preload = 'auto';
    v.muted = true;
    v.load();
    v.play().then(() => setTimeout(() => { try { v.pause(); } catch { } }, 120)).catch(() => { });

    const markReady = () => {
      this.readyByUrl.set(url, true);
      if (this.currentUrlStream === url) this.isBuffering = false;
      v.removeEventListener('canplaythrough', markReady);
      v.removeEventListener('loadeddata', markReady);
    };
    v.addEventListener('canplaythrough', markReady);
    v.addEventListener('loadeddata', markReady);

    setTimeout(() => {
      if (!this.readyByUrl.get(url)) this.readyByUrl.set(url, true);
      if (this.currentUrlStream === url) this.isBuffering = false;
    }, 3500);
  }

  playVideo(url: string): void {
    if (!url) return;
    this.currentUrlStream = url;
    this.videoViewURL = url;

    this.primeVideo(url);
    ($('#videoPlayerModal') as any).modal('show');

    setTimeout(() => {
      const el = this.player?.nativeElement;
      if (!el) return;
      el.load();
      if (this.readyByUrl.get(url)) {
        this.isBuffering = false;
        el.play().catch(() => { });
      } else {
        this.isBuffering = true;
      }
    }, 0);
  }

  closeVideoModal(): void {
    ($('#videoPlayerModal') as any).modal('hide');
    const el = this.player?.nativeElement;
    if (el) {
      try { el.pause(); } catch { }
      el.currentTime = 0;
      el.removeAttribute('src');
      el.load();
    }
    this.currentUrlStream = null;
    this.isBuffering = true;
  }

  onPlayerCanPlay(): void { this.isBuffering = false; }
  onPlaying(): void { this.isBuffering = false; }
  onWaiting(): void { this.isBuffering = true; }

  // ===========================================================================
  // Branding & location
  // ===========================================================================
  getFreelancerBrandDetails(transferKeyCode: any): void {
    this.currentUrl = window.location.origin;
    this.currentUrlLocation = window.location.href;

    const jSon = { 
      // brand_url: 'farha.net0.download',
      brand_url: this.url, 
      key: transferKeyCode 
    };

    this.apiService.getFreelancerDetails(jSon).subscribe((res: any) => {
      if (res.success == 0) {
        this.router.navigate(['/page-not-found']);
        this.titleService.setTitle('Transfer Center Download');
        return;
      }

      if (res.success == 1) {
        this.loader = false;
        this.currentUser = res.data;
        this.logoUrl = this.currentUser.logo_url;
        this.companyName = this.currentUser.company_name;
        this.companyOwnWebsite = this.ensureUrlHasProtocol(this.resolveWebsiteUrl());
        this.applyBrandColors();

        const orgName = this.currentUser.organization_name;
        this.titleService.setTitle(orgName ? orgName : 'Transfer Center Download');
      }
    }, err => console.error(err));
  }

  /** Picks the company website URL based on user type / custom hyperlink. */
  private resolveWebsiteUrl(): string {
    const customUrl = this.currentUser.company_website_hyperlink;
    if (customUrl) {
      return customUrl;
    }
    const isMaster = this.currentUser.is_master_user == 1 || this.currentUser.is_master_user == '1';
    return isMaster ? 'https://transfercenter.jp/business.html' : 'https://transfercenter.jp';
  }

  private applyBrandColors(): void {
    const bgColor = this.currentUser.button_background_color;
    const color = (bgColor != null && bgColor != 'null' && bgColor != '') ? bgColor : this.buttonBGselectedColor;
    this.borderTopColor = '1px solid ' + color;
    this.borderBottomColor = '1px solid ' + color;
  }

  ensureUrlHasProtocol(url: string): string {
    if (!url) {
      return 'https://transfercenter.jp';
    }
    // Already has a scheme (e.g. http:, https:).
    if (url.match(/^[a-zA-Z][a-zA-Z\d+\-.]*:/)) {
      return url;
    }
    return 'https://' + url;
  }

  getLocations(): void {
    this.loadGeoIpPlugin();
    this.apiService.getGeoLocation('').pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(() => this.cdr.markForCheck())
    ).subscribe((location: any) => {
      this.ipaddress = location.ip || 'Unknown';
      this.city = location.city || 'Unknown';
      this.state = location.state || 'Unknown';
      this.country = location.country || 'Unknown';
    });
  }

  /**
   * Loads the geoip2 plugin on demand. Loading it here (instead of statically in
   * index.html) keeps it out of the page when downloader info is disabled.
   */
  private loadGeoIpPlugin(): void {
    const pluginSrc = 'https://geoip-js.com/js/apis/geoip2/v2.1/geoip2.js';
    if (document.querySelector(`script[src="${pluginSrc}"]`)) {
      return;
    }
    const script = document.createElement('script');
    script.src = pluginSrc;
    script.type = 'text/javascript';
    document.body.appendChild(script);
  }

  // ===========================================================================
  // Misc helpers
  // ===========================================================================
  copyText(hashValue: string): void {
    this.toast.showSuccess(this.translateService.instant('sha_value_copied'), '');
    navigator.clipboard.writeText(hashValue).catch(() => console.error('Unable to copy text'));
  }

  /** Flags iOS/iPadOS touch devices, which need the native download fallback. */
  private detectIosTouchDevice(): void {
    const userAgent = navigator.userAgent;
    const platform = (navigator as any).userAgentData?.platform || navigator.platform;

    const isTouchDevice = 'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      (navigator as any).msMaxTouchPoints > 0;

    const isIOS = /iPad|iPhone|iPod/.test(userAgent) ||
      (platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    const isIPadOS = (platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
      /iPad/.test(userAgent);

    this.isIosTouchDevice = isTouchDevice && (isIOS || isIPadOS);
  }

  /** Builds an ng-otp-input config for an OTP of the given length. */
  private buildOtpConfig(length: number, allowNumbersOnly = true) {
    return {
      // When false the boxes accept alphanumeric input. `letterCase` is left
      // unset so the value is preserved exactly as typed (case-sensitive).
      allowNumbersOnly,
      length,
      isPasswordInput: false,
      disableAutoFocus: false,
      placeholder: '',
      inputStyles: {
        'width': '74px',
        'height': '107px',
        'font-size': '28px',
        'color': '#2196f3',
        'font-weight': '600',
        'border-radius': '8px'
      }
    };
  }

  private calculateRemainingTime(progress: number, elapsedTime: number): string {
    if (progress === 0) {
      return '';
    }
    const totalEstimatedTime = (elapsedTime / progress) * 100;
    return this.formatTime(totalEstimatedTime - elapsedTime);
  }

  private formatTime(milliseconds: number): string {
    const seconds = Math.floor((milliseconds / 1000) % 60);
    const minutes = Math.floor((milliseconds / (1000 * 60)) % 60);
    const hours = Math.floor((milliseconds / (1000 * 60 * 60)) % 24);

    const hoursDisplay = hours > 0 ? hours + 'h ' : '';
    const minutesDisplay = minutes > 0 ? minutes + 'm ' : '';
    const secondsDisplay = seconds > 0 ? seconds + 's' : '';

    return hoursDisplay + minutesDisplay + secondsDisplay || '0';
  }

  private padNumber(value: number): string {
    return value < 10 ? '0' + value : '' + value;
  }

  /** Builds a "YYYYMMDDHHmmss" string used to name the ZIP download. */
  private currentTimestampString(): string {
    const d = new Date();
    return `${d.getFullYear()}${this.padNumber(d.getMonth() + 1)}${this.padNumber(d.getDate())}` +
      `${this.padNumber(d.getHours())}${this.padNumber(d.getMinutes())}${this.padNumber(d.getSeconds())}`;
  }
}
